import { Component, forwardRef, Input, OnInit, OnDestroy } from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
} from '@angular/forms';
import { FormUtilityService } from 'src/app/modules/core/services/form-utility.service';
import { Subscription } from 'rxjs';
import { NgIf, NgClass, NgFor } from '@angular/common';
import { BeyFieldErrorMessageComponent } from '../bey-field-error-message/bey-field-error-message.component';

/****
 *  Optionally we can use [(ngModel)]="var" on this component
 */
const noop = () => {};
export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BeySelectComponent),
  multi: true,
};

@Component({
  selector: 'bey-select',
  templateUrl: './bey-select.component.html',
  styleUrls: ['./bey-select.component.scss'],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
  standalone: true,
  imports: [NgIf, NgClass, ReactiveFormsModule, FormsModule, NgFor, BeyFieldErrorMessageComponent],
})
export class BeySelectComponent implements OnInit, ControlValueAccessor, OnDestroy {
  /**
   * Placeholder text
   * */
  @Input()
  placeholder: string;

  /**
   * Options to select from
   * */
  @Input()
  options: Array<object>;

  /**
   * Label for the select field
   * */
  @Input()
  label?: string;

  /**
   * The property used to bind the label for an option
   * */
  @Input()
  bindLabel: string;

  /**
   * The property used to bind the value for an option
   * */
  @Input()
  bindValue: string;

  /**
   * Display only border bottom for the select field
   * */
  @Input()
  borderBottom: boolean = true;

  /**
   * Select box bordered style
   * */
  @Input()
  bordered: boolean = true;

  /**
   * Hide the validation feedback in case of an error
   * */
  @Input()
  hideValidationFeedback: boolean = false;

  /**
   * Flag to mark the component as loading
   * */
  @Input()
  isLoading: boolean = false;

  /****
   * Class list to override in form of object
   * for the following elements: label
   */
  @Input()
  classList: { [label: string]: string };

  /****
   * formControl instance
   */
  @Input()
  control: AbstractControl;

  displayError: boolean = false;
  errorMessage: string = '';
  subs$: Subscription = new Subscription();

  constructor(private formUtility: FormUtilityService) {}
  ngOnInit(): void {
    if (!!this.control) {
      this.subs$.add(
        this.control.valueChanges.subscribe(() => {
          this.updateErrorStatus();
        })
      );
    }
  }

  updateErrorStatus(): void {
    if (this.control) {
      this.control.markAsTouched();
      this.errorMessage = this.formUtility.getFieldErrorMessage(this.control);
      this.displayError = this.formUtility.getFieldValidation(this.control);
    }
  }

  //The internal data model
  private innerValue: any = null;

  //Placeholders for the callbacks which are later provides
  //by the Control Value Accessor
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  //get accessor
  get value(): any {
    return this.innerValue;
  }

  //set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChangeCallback(v);
    }
  }

  //Set touched on blur
  onBlur() {
    this.onTouchedCallback();
    this.updateErrorStatus();
  }

  //From ControlValueAccessor interface
  writeValue(value: any) {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
  }

  //From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  //From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  ngOnDestroy(): void {
    this.subs$.unsubscribe();
  }
}
